import React, { useState, useEffect, useMemo, useRef } from 'react';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import ActivityLogs from './ActivityLogs';
import { Button, Checkbox, FormControlLabel, FormGroup, IconButton, Pagination, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { useSelector } from 'react-redux';
import { timeAgo, convertSessionTime, formatDate } from '../../utils/dateTime';
import { createEmailConfig, fetchEmailsList } from '../../api/email';
import SuspendCard from './SuspendCard';
import ProfileCard from './ProfileCard';
import Loader from '../common/Loader/Loader';
import { FRONTEND_URL } from '../../config/config';
import { loginEmail } from '../../api/auth';
import { useNotification } from '../../hooks/useNotification';
import UserTableSkeleton from './UserTableSkeleton';
import Tooltip from '@mui/material/Tooltip';
const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 700,
    width: '95%',
    margin: '0 auto',
    bgcolor: 'background.paper',
    borderRadius: '20px',
    boxShadow: 24,
    p: '32px 32px 0 32px',
    maxHeight: '80vh', // Adjust the height for scrolling
    overflow: 'auto', // Make content scrollable
};
export const style2 = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1300,
    width: '95%',
    margin: '0 auto',
    bgcolor: 'background.paper',
    borderRadius: '20px',
    boxShadow: 24,
    p: '0 30px 30px 30px',
    maxHeight: '80vh', // Adjust the height for scrolling
    overflow: 'auto', // Make content scrollable
};

const EmailList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [openSuspend, setOpenSuspend] = useState(false);
    const [openProfile, setOpenProfile] = useState(false);
    const [emailData, setEmailData] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState({
        email: true,
        isActive: true
    });

    const tableContainerRef = useRef(null);
const scrollThumbRef = useRef(null);
const [scrollState, setScrollState] = useState({
  scrollLeft: 0,
  scrollWidth: 0,
  clientWidth: 0,
});

    const { filter } = useSelector((state) => state);
    const [selectedEmail, setSelectedEmail] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(25);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEmails, setTotalEmails] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const { showErrorNotification, showSuccessNotification } = useNotification();
    const [emailModal, setEmailModal] = useState(false)
    const [emailConfig, setEmailConfig] = useState({
        email: '',
        smtpServer: '',
        port: 25,
        secure: false,
        username: '',
        password: '',
        isActive: true,
    });

    const onOpenEmailModal = () => {
        setEmailModal(true)
    }
    const handleEmailClose = () => {
        setEmailModal(false)
    }


    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEmailConfig({
            ...emailConfig,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await fetchEmailsList(
                setEmailData
            );
            setTotalEmails(data.totalEmailsCount);
            setTotalPages(data.totalPages);
        } catch (err) {
            console.error('Error while loading user data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [filter, openSuspend, currentPage]);

    const handleOpen = (user) => {
        setSelectedEmail(user);
        setOpen(true);
    };

    const handleOpenSuspend = (user) => {
        setSelectedEmail(user);
        setOpenSuspend(true);
    };

    const handleOpenProfile = (user) => {
        setSelectedEmail(user);
        setOpenProfile(true);
    };


    const filteredData = emailData
    .filter((user) =>
      Object.values(user).some(
        (value) =>
          typeof value === 'string' &&
          value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };


    const columnHeaders = [
        { key: 'email', label: 'Email' },
        { key: 'isActive', label: 'Active' },
    ];

    const handleSearch = () => {
        fetchData()
    }

    const handleColumnToggle = (key) => {
        setVisibleColumns((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleAddEmail = () => {
        setEmailModal(true)
    }

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await createEmailConfig(emailConfig);
            handleEmailClose();
            showSuccessNotification('Email config save successfully')
            fetchData();
        } catch (err) {
            console.error('Error creating email config', err);
        } finally {
            setLoading(false);
        }
    };



    // Scroll thumb width calculation
const thumbWidth = useMemo(() => {
  if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
    const ratio = scrollState.clientWidth / scrollState.scrollWidth;
    return Math.max(ratio * scrollState.clientWidth, 50);
  }
  return 100;
}, [scrollState]);

// Scroll thumb position
const thumbPosition = useMemo(() => {
  if (scrollState.scrollWidth > 0 && scrollState.clientWidth > 0) {
    const ratio = scrollState.scrollLeft / (scrollState.scrollWidth - scrollState.clientWidth);
    return ratio * (scrollState.clientWidth - thumbWidth);
  }
  return 0;
}, [scrollState, thumbWidth]);

const updateScrollState = () => {
  if (tableContainerRef.current) {
    const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
    setScrollState({
      scrollLeft,
      scrollWidth,
      clientWidth,
    });
  }
};

const handleTableScroll = () => {
  updateScrollState();
};

const handleStickyScroll = (e) => {
  if (!tableContainerRef.current) return;

  const track = e.currentTarget;
  const trackRect = track.getBoundingClientRect();
  const clickX = e.clientX - trackRect.left;
  const thumbCenter = thumbWidth / 2;
  
  const newScrollLeft = (clickX - thumbCenter) / (trackRect.width - thumbWidth) * 
                       (scrollState.scrollWidth - scrollState.clientWidth);
  
  tableContainerRef.current.scrollLeft = newScrollLeft;
};

const handleThumbDrag = (e) => {
  e.preventDefault();
  const startX = e.clientX;
  const startScrollLeft = scrollState.scrollLeft;

  const handleMouseMove = (moveEvent) => {
    const deltaX = moveEvent.clientX - startX;
    const trackWidth = scrollState.clientWidth - thumbWidth;
    const scrollableWidth = scrollState.scrollWidth - scrollState.clientWidth;
    
    if (trackWidth > 0) {
      const newScrollLeft = startScrollLeft + (deltaX / trackWidth) * scrollableWidth;
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollLeft = Math.max(0, Math.min(newScrollLeft, scrollableWidth));
      }
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);
};
useEffect(() => {
  updateScrollState();
  window.addEventListener('resize', updateScrollState);
  
  return () => {
    window.removeEventListener('resize', updateScrollState);
  };
}, [emailData, visibleColumns]);
    return (
        <>
            <div className="mb-16 d-flex justify-content-between align-items-center">
                <h6 className="text-lg mb-0">Email List</h6>

                <div className="d-flex align-items-center gap-2">
                    <form className="navbar-search">
                        <input
                            type="text"
                            name="search"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="icon">
                            <iconify-icon icon="ion:search-outline" onClick={handleSearch}></iconify-icon>
                        </span>
                    </form>
                    <div className="dropdown">
                        <Tooltip title="Manage Columns" arrow placement="top">
    <button
      className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
      type="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
    >
      <ViewColumnOutlinedIcon />
    </button>
  </Tooltip>
                        <ul className="dropdown-menu manage-list">
                            {columnHeaders.map((column) => (
                                <li
                                    key={column.key}
                                    className="dropdown-item d-flex align-items-center gap-2"
                                >
                                    <input
                                        className="form-check-input"
                                        style={{ width: '18px', height: '18px' }}
                                        type="checkbox"
                                        checked={visibleColumns[column.key]}
                                        onChange={() => handleColumnToggle(column.key)}
                                    />
                                    {column.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <button
                className={`badge text-md fw-medium px-20 py-9 radius-8 bg-light-100 text-black mb-16`}
                // bg-warning-900 text-white
                // }}
                onClick={() => handleAddEmail()}
            >
                Add
            </button>

          <div className="table-scroll-container">
  <div 
    ref={tableContainerRef}
    className="custom-horizontal-scroll scroll-sm"
    onScroll={handleTableScroll}
  >
    <table className="table bordered-table sm-table mb-0">
                    <thead>
                        <tr style={{ background: '#f4f4f4', textAlign: 'left' }}>
                            <th>#</th>
                            {visibleColumns.email && <th className="text-center">Email</th>}
                            {visibleColumns.isActive && <th className="text-center">Active</th>}
                        </tr>
                    </thead>
                    <tbody>
                           {loading ? (
    <UserTableSkeleton
      rows={10}
      columns={Object.keys(visibleColumns).filter((key) => visibleColumns[key]).length + 1}
    />
  ) : filteredData.length > 0 ? (
                            filteredData.map((user, index) => (
                                <tr key={index}>
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    {visibleColumns.email && (
                                        <td className="text-center">{user.email}</td>
                                    )}
                                    {visibleColumns.isActive && (
                                        <td className="text-center">{user.isActive ? 'true' : 'false'}</td>
                                    )}
                                </tr>
                            ))
                        ) : (
    <tr>
      <td colSpan="20" className="text-center py-3">
        No data found
      </td>
    </tr>
  )}

                    </tbody>
                </table>
            </div>
             {/* Sticky Scroll Bar */}
  {scrollState.scrollWidth > scrollState.clientWidth && (
    <div className="sticky-scroll-container">
      <div 
        className="sticky-scroll-track"
        onClick={handleStickyScroll}
      >
        <div
          ref={scrollThumbRef}
          className="sticky-scroll-thumb"
          style={{
            width: `${thumbWidth}px`,
            transform: `translateX(${thumbPosition}px)`
          }}
          onMouseDown={handleThumbDrag}
        />
      </div>
    </div>
  )}
</div>
            {/* Pagination */}
            <div className="d-flex justify-content-center mt-16">
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    sx={{
    "& .MuiPaginationItem-root": {
      color: "#000",  
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important",  
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }} 
                />
            </div>

            <Modal
                open={emailModal}
                onClose={handleEmailClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Typography variant="h6" id="modal-modal-title" sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}>
                        Add Email Configuration
                    </Typography>

                    <form onSubmit={handleFormSubmit}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            fullWidth
                            name="email"
                            value={emailConfig.email}
                            onChange={handleChange}
                            size="small" 
                             sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />
                        <TextField
                            label="SMTP Server"
                            variant="outlined"
                            fullWidth
                            name="smtpServer"
                            value={emailConfig.smtpServer}
                            onChange={handleChange}
                            size="small"
                            sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />
                        <TextField
                            label="Port"
                            type="number"
                            variant="outlined"
                            fullWidth
                            name="port"
                            value={emailConfig.port}
                            onChange={handleChange}
                            size="small"
                            sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={emailConfig.secure}
                                    onChange={handleChange}
                                    name="secure"
                                    color="primary"
                                />
                            }
                            label="Secure"
                            sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />
                        <TextField
                            label="Username"
                            variant="outlined"
                            fullWidth
                            name="username"
                            value={emailConfig.username}
                            onChange={handleChange}
                            size="small"
                            sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />
                        <TextField
                            label="Password"
                            type="password"
                            variant="outlined"
                            fullWidth
                            name="password"
                            value={emailConfig.password}
                            onChange={handleChange}
                            size="small"
                            sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={emailConfig.isActive}
                                    onChange={handleChange}
                                    name="isActive"
                                    color="primary"
                                />
                            }
                            label="Active"
                            sx={{
    marginBottom: '16px',
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '12px 10px',
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
    },
  }}
                        />

                        <Box
                            className="border-top mt-16 position-sticky bottom-0 z-3 bg-base"
                            sx={{ marginTop: '20px', background: '#fff', paddingBottom:'32px' }}
                        >
                            <Button
                                onClick={handleEmailClose}
                                className="w-100"
                                sx={{
                                    background: '#fff',
                                    color: '#000',
                                    fontSize: '16px',
                                    textTransform: 'capitalize',
                                    fontFamily: 'Neue Montreal',
                                    marginBottom: '10px',
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                type="submit"
                                className="w-100"
                                sx={{
                                    background: '#916008',
                                    color: '#fff',
                                    fontSize: '16px',
                                    py:'12px',
                                    textTransform: 'capitalize',
                                    fontFamily: 'Neue Montreal',
                                }}
                            >
                                Save Configuration
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>
        </>
    );
};

export default EmailList;
